import { FC, useCallback, useEffect, useState } from "react";
import CodeSpinner from "../../components/CodeSpinner";
import { ShowToast } from "../../components/CodeToast";
import { Popup } from "../../components/Popup";
import { FormStateType, UserType } from "../../enums";
import { CredenciadaModel } from "../../models/credenciada";
import { CredenciadaService } from "../../services/CredenciadaService";
import { ModalProps } from "../../types";
import { CredenciadasModal } from "./components/Modal";

import { KeyboardEvent } from "react";
import { CodeDataGrid } from "../../components/CodeDataGrid";
import { ICodeDataGridActionButton, ICodeDataGridPagination, ICodeDataGridRow } from "../../components/CodeDataGrid/types";
import { useCredenciadaColumns } from "../../models/credenciada/useCredenciadaColumns";
import { SessionManager } from "../../utils/SessionManager";
import { useBuscarPermissao } from "../../hooks/useBuscarPermissao";

export const CredenciadasPage: FC = () => {
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [dataSource, setDataSource] = useState<CredenciadaModel[]>([]);

  const [page, setPage] = useState<number>(1);
  const [pagination, setPagination] = useState<Omit<ICodeDataGridPagination, 'onPageChanged'>>({ page: 1, pageCount: 0, limit: 100, recordCount: 0 });
  const [selectedRow, setSelectedRow] = useState<ICodeDataGridRow>();

  const [selectedModel, setSelectedModel] = useState<CredenciadaModel | null>(null);
  const [showPopupDelete, setShowPopupDelete] = useState<boolean>(false);
  const [modalState, setModalState] = useState<ModalProps>({
    show: false,
    state: FormStateType.view,
    id: null,
  });

  const userSession = SessionManager.getSession().user;
  const { permissao } = useBuscarPermissao('credenciadas');


  const onFormLoad = useCallback(async () => {
    setIsLoadingData(true);
    CredenciadaService.get({ page: page, limit: 100 })
      .then((response) => {
        setDataSource(response.data ?? []);

        setPagination({
          page: response.pagination.page,
          pageCount: response.pagination.pageCount,
          limit: response.pagination.limit,
          recordCount: response.pagination.recordCount
        });
      })
      .finally(() => setIsLoadingData(false));
  }, [setDataSource, setIsLoadingData, setPagination, page]);

  useEffect(() => {
    onFormLoad();
  }, [onFormLoad]);

  const add = () => setModalState({ ...modalState, state: FormStateType.add, show: true, id: 0 });

  const viewOrEdit = (edit: boolean) => {
    if (selectedModel?.id === undefined) {
      ShowToast({
        message: `Nenhum registro selecionado para ${edit ? "edição" : "visualização"
          }`,
      });
      return;
    }

    setModalState({
      state: edit ? FormStateType.edit : FormStateType.view,
      show: true,
      id: selectedModel.id,
    });
  };

  const deleteItem = async () => {
    try {
      let response = await CredenciadaService.delete(selectedModel!.id!);
      if (!response.success) {
        console.log(response.messages);
        return;
      }

      await onFormLoad();
    } catch (error) {
      console.log(error);
    } finally {
      setShowPopupDelete(false);
    }
  };

  const adminToolbarActions : ICodeDataGridActionButton[] = userSession?.tipoUsuario !== UserType.admin ? [] : [
    { label: 'Inserir', style: 'colored', disabled: !permissao?.inserir, onClick: add },
    { label: 'Editar', style: 'flat', onClick() { viewOrEdit(true) }, disabled: selectedRow === undefined || !permissao?.editar },
    { label: 'Excluir', style: 'destructive', onClick() { setShowPopupDelete(true) }, disabled: selectedRow === undefined || !permissao?.excluir },
  ]

  const handleKeydownEvent = (e: KeyboardEvent<HTMLDivElement> | undefined) => {
    if (e?.code?.toLowerCase() === "escape") {
      setModalState({ ...modalState, show: false, id: null });
      return;
    }
  }

  return (
    <div onKeyDown={handleKeydownEvent}>
      <CodeSpinner hidden={!isLoadingData} fullPage={true} />

      <div className="mt-5" hidden={isLoadingData}>
        <CodeDataGrid
          sort={{ columnIndex: 2, direction: "asc", columnProperty: "nomeCompleto" }}
          datasource={dataSource}
          striped={true}
          header={{
            title: "Credenciadas",
            subtitle: "Listagem de credenciadas ativas e inativas",
            filter: true
          }}
          actions={[
            ...adminToolbarActions,
            { label: 'Consultar', style: 'flat', onClick() { viewOrEdit(false) }, disabled: selectedRow === undefined|| !permissao?.consultar  },
          ]}
          columns={useCredenciadaColumns()}
          onRowSelect={(item) => { setSelectedModel(item.selected ? item.item : null); setSelectedRow(item.selected ? item : undefined); }}
          pagination={{ ...pagination, onPageChanged: (page) => setPage(page) }}
        />

        <CredenciadasModal
          show={modalState.show}
          id={modalState.id}
          state={modalState.state}
          onClose={() => {
            setModalState({ ...modalState, show: false, id: null });
          }}
          onSave={async () => {
            setModalState({ ...modalState, show: false, id: null });
            await onFormLoad();
          }}
        />

        <Popup
          isShowing={showPopupDelete}
          message="Confirma a exclusão do registro selecionado?"
          isDestructive={true}
          onConfirm={async () => await deleteItem()}
          onCancel={() => setShowPopupDelete(false)}
        />
      </div>
    </div>
  );
};
